import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import {
  getMappedFields,
  getMappedHeaders,
  hasLinkedInUrl,
} from '../../_shared/domain/file-import';
import { canUploadFile } from '../domain/file-import.validation';

const selectFileImport = (state: RootState) => state.fileImport;

export const selectListId = (state: RootState) => selectFileImport(state).listId;
export const selectIsClientMode = (state: RootState) => selectFileImport(state).listId === null;
export const selectStep = (state: RootState) => selectFileImport(state).step;
export const selectError = (state: RootState) => selectFileImport(state).error;

// ── Upload ──
export const selectImportName = (state: RootState) => selectFileImport(state).importName;
export const selectDataSource = (state: RootState) => selectFileImport(state).dataSource;
/**
 * The Upload fields (Import name, Data Source) are locked while the file is
 * parsed (#749): the data source the dropzone opened on stays complete until
 * Map Columns.
 */
export const selectUploadFieldsLocked = (state: RootState) => selectFileImport(state).parsing;

/** Client mode: the dropzone opens once the data source is complete (#749). */
export const selectCanUpload = (state: RootState) =>
  canUploadFile({ listId: selectListId(state), dataSource: selectDataSource(state) });
export const selectFileName = (state: RootState) => selectFileImport(state).fileName;
export const selectParsing = (state: RootState) => selectFileImport(state).parsing;
export const selectParseResult = (state: RootState) => selectFileImport(state).parseResult;

// ── Map columns ──
export const selectColumnMapping = (state: RootState) => selectFileImport(state).columnMapping;

/** Profile fields already mapped (drives select disabling + the "N mapped" chip). */
export const selectMappedFields = createSelector(selectColumnMapping, getMappedFields);

/** Analyze requires a linkedin_url mapping. */
export const selectHasLinkedInUrl = createSelector(selectColumnMapping, hasLinkedInUrl);

/** Mapped headers in file order (columns of the preview table). */
export const selectMappedHeaders = createSelector(
  (state: RootState) => selectParseResult(state)?.headers,
  selectColumnMapping,
  (headers, mapping) => getMappedHeaders(headers ?? [], mapping),
);

// ── Analyze ──
export const selectAnalyzing = (state: RootState) => selectFileImport(state).analyzing;
export const selectAnalyzeResult = (state: RootState) => selectFileImport(state).analyzeResult;
export const selectIsEnriched = (state: RootState) => selectFileImport(state).isEnriched;
export const selectOverwriteRows = (state: RootState) => selectFileImport(state).overwriteRows;

/** The import button is enabled only when the analyze found something to write. */
export const selectCanImport = (state: RootState) => {
  const analyze = selectAnalyzeResult(state);
  return analyze !== null && analyze.to_create + analyze.to_update > 0;
};

// ── Import ──
export const selectImporting = (state: RootState) => selectFileImport(state).importing;
export const selectImportResult = (state: RootState) => selectFileImport(state).importResult;
